import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router';

import { useIsMobile } from '~/core/hooks/useMediaQuery';
import { getProductionHeadRouteSkeleton } from '~/features/production_head/presentation/components/phScreenSkeletons';
import { useLayoutStore } from '~/shared/stores/layoutStore';

import { getRouteSkeleton } from './route_skeleton_registry';

export function GlobalLoadingScreen() {
  const navigation = useNavigation();
  const location = useLocation();
  const isPendingNavigation = useLayoutStore((state) => state.isPendingNavigation);
  const [shouldRender, setShouldRender] = useState(true);
  const [opacity, setOpacity] = useState(1);
  const { t } = useTranslation(['common']);

  // Picking a season/plan on the Season Plan picker navigates within a
  // stable parent layout (see season_plan_index_page.tsx), not to a
  // different page - including the very first pick, from the bare index
  // path with no season yet. Treating any of those like a real route change
  // flashed the full-screen loader over an otherwise unchanged layout.
  // Scoped to exactly the picker's own path shape (index, season, or
  // season/plan) so it does NOT swallow navigation into version/revision
  // detail pages (those still need their own loading UI) or the separate
  // create-plan page.
  const seasonPlanPickerPattern =
    /^\/agronomist-season-plan(?:\/(?!create-plan)[^/]+(?:\/[^/]+)?)?$/;
  const isWithinSeasonPlanPicker =
    seasonPlanPickerPattern.test(location.pathname) &&
    !!navigation.location &&
    seasonPlanPickerPattern.test(navigation.location.pathname);

  const isRouteChanging =
    navigation.state !== 'idle' &&
    navigation.location &&
    navigation.location.pathname !== location.pathname &&
    !isWithinSeasonPlanPicker;

  const isInitialLoad = !location.key || location.key === 'default';
  const isActive = isInitialLoad || isPendingNavigation || isRouteChanging;

  // A Production Head screen shows its own skeleton instead of the generic spinner, both for a
  // client-side navigation to it and for the first document load of it. Other routes and
  // SecureLink pending keep the spinner. The last skeleton is kept while the overlay fades out so
  // it does not flash empty.
  const skeletonTarget =
    isPendingNavigation || !isActive
      ? null
      : isRouteChanging && navigation.location
        ? navigation.location
        : isInitialLoad
          ? location
          : null;
  const destinationSkeleton = skeletonTarget
    ? getProductionHeadRouteSkeleton(skeletonTarget.pathname, skeletonTarget.search)
    : null;
  const lastSkeletonRef = useRef<React.ReactNode>(null);
  if (destinationSkeleton) lastSkeletonRef.current = destinationSkeleton;
  const skeleton = destinationSkeleton ?? (isActive ? null : lastSkeletonRef.current);
  const showFullLoader = (isInitialLoad || isPendingNavigation || isRouteChanging) && !skeleton;

  const isSidebarOpen = useLayoutStore((state) => state.isSidebarOpen);
  const isMobile = useIsMobile();

  // Prefer the destination page's own skeleton over the generic spinner, so
  // the user sees the shape of what they're navigating to instead of a
  // blank loading screen that gives no hint of the page underneath.
  const destinationPathname = navigation.location?.pathname ?? location.pathname;
  const DestinationSkeleton = getRouteSkeleton(destinationPathname);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isActive) {
      setShouldRender(true);
      setOpacity(1);
    } else {
      setOpacity(0);
      timeout = setTimeout(() => {
        setShouldRender(false);
        lastSkeletonRef.current = null;
      }, 200);
    }

    return () => clearTimeout(timeout);
  }, [isActive]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col transition-opacity duration-200 ease-in-out ${
        skeleton ? 'overflow-y-auto' : 'items-center justify-center'
      } ${isActive ? '' : 'pointer-events-none'}`}
      style={{
        opacity,
        backgroundColor: '#f3f4f4',
        top: '80px', // Below the TopBar (h-20)
        left: isSidebarOpen && !isMobile ? '320px' : isMobile ? '0' : '96px',
      }}
    >
      {showFullLoader && DestinationSkeleton && (
        <div className="h-full w-full overflow-y-auto px-4 py-8 md:px-8">
          <DestinationSkeleton />
        </div>
      )}

      {/* Generic spinner + text fallback for routes without their own skeleton yet */}
      {showFullLoader && !DestinationSkeleton && (
        <div className="relative flex flex-col items-center space-y-8">
          <div className="logo-pulse mb-2">
            <div className="windows-loader">
              <div />
              <div />
              <div />
              <div />
              <div />
            </div>
          </div>
          <div className="text-center">
            <p className="bodyLg font-semibold tracking-wide text-primarySemiDark">
              {t('loadingContent')}
            </p>
            <p className="bodySm text-primarySemiDark/60 mt-1">{t('loadingContentDescription')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
